<template>
  <div class="wrapper">
    <admin-title :title="$route.meta.name" />

    <div class="area-layout">
      <!-- 左侧区域列表 -->
      <div class="area-list-panel">
        <div class="panel-header">
          <span class="panel-title">区域列表</span>
          <el-button style="padding: 8px;" type="success" size="mini" @click="openAddTopArea">新增大区</el-button>
        </div>

        <div class="area-scroll">
          <div
            v-for="topArea in topAreaList"
            :key="topArea.id"
            class="top-area-item"
            :class="{ active: activeTopAreaId === topArea.id && activeSubAreaId === null }"
            @click="selectTopArea(topArea)"
          >
            <div class="top-area-header">
              <span class="top-area-name">{{ topArea.areaName }}</span>
              <el-button
                style="padding: 5px;"
                type="primary"
                size="mini"
                icon="el-icon-plus"
                circle plain
                @click.stop="openAddSubArea(topArea)"
              />
            </div>
            <div
              v-for="sub in topArea.subAreaList"
              :key="sub.id"
              class="sub-area-item"
              :class="{ active: activeSubAreaId === sub.id }"
              @click.stop="selectSubArea(sub, topArea)"
            >
              <span>{{ sub.areaName }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧详情面板 -->
      <div class="detail-panel" v-if="activeTopAreaId || activeSubAreaId">
        <!-- 大区详情 -->
        <template v-if="activeSubAreaId === null && topAreaDetail">
          <div class="detail-header">
            <h2 class="detail-title">{{ topAreaDetail.areaName }}</h2>
            <div class="detail-actions">
              <el-button
                type="danger"
                size="small"
                :disabled="topAreaDetail.subAreaList && topAreaDetail.subAreaList.length > 0"
                @click="handleDeleteTopArea"
              >删除</el-button>
              <el-button type="primary" size="small" @click="openEditTopArea">编辑</el-button>
              <el-button size="small" @click="closeDetail">关闭</el-button>
            </div>
          </div>
          <div class="detail-body">
            <div class="info-row">
              <span class="label">大区跟进人</span>
              <span>{{ topAreaDetail.followUserName || '-' }}</span>
            </div>
            <div class="province-table-wrap">
              <el-table :data="topAreaDetail.provinceCityStatList" border size="small">
                <el-table-column label="省份" prop="provinceName" width="120" />
                <el-table-column label="城市数" prop="cityCount" width="80" />
                <el-table-column label="城市明细">
                  <template slot-scope="{ row }">{{ row.cityNameList.join('、') }}</template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </template>

        <!-- 分区详情 -->
        <template v-if="activeSubAreaId !== null && subAreaDetail">
          <div class="detail-header">
            <h2 class="detail-title">{{ subAreaDetail.areaName }}</h2>
            <div class="detail-actions">
              <el-button type="danger" size="small" @click="handleDeleteSubArea">删除</el-button>
              <el-button type="primary" size="small" @click="openEditSubArea">编辑</el-button>
              <el-button size="small" @click="closeDetail">关闭</el-button>
            </div>
          </div>
          <div class="detail-body">
            <div class="info-row">
              <span class="label">分区跟进人</span>
              <span style="font-weight: bold">{{ subAreaDetail.followUserName || '-' }}</span>
            </div>
            <div class="section-title">二级分区下辖管理</div>
            <el-table :data="subAreaDetail.provinceCityStatList" border size="small">
              <el-table-column label="省份" prop="provinceName" width="120" />
              <el-table-column label="城市数" prop="cityCount" width="80" />
              <el-table-column label="城市明细">
                <template slot-scope="{ row }">{{ row.cityNameList.join('、') }}</template>
              </el-table-column>
            </el-table>
          </div>
        </template>
      </div>
      <div class="detail-panel empty" v-else />
    </div>

    <!-- 新增/编辑大区 dialog -->
    <el-dialog
      :title="topAreaForm.id ? '编辑大区' : '新增大区'"
      :visible.sync="topAreaDialogVisible"
      width="500px"
      @close="resetTopAreaForm"
    >
      <el-form ref="topAreaForm" :model="topAreaForm" :rules="topAreaRules" label-width="100px">
        <el-form-item label="大区名称" prop="areaName">
          <el-input v-model="topAreaForm.areaName" placeholder="请输入" size="small" />
        </el-form-item>
        <el-form-item label="大区跟进人" prop="followUserId">
          <SelectAdminUser
            v-model="topAreaForm.followUserId"
            :job-code="AdminJobCode.A001"
            width="100%"
            @input="onTopAreaFollowUserSelect"
          />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="topAreaDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="topAreaSaving" @click="saveTopArea">保存</el-button>
      </div>
    </el-dialog>

    <!-- 新增/编辑分区 dialog -->
    <el-dialog
      :title="subAreaForm.id ? '编辑二级分区' : '新增二级分区'"
      :visible.sync="subAreaDialogVisible"
      width="700px"
      @close="resetSubAreaForm"
    >
      <el-form ref="subAreaForm" :model="subAreaForm" :rules="subAreaRules" label-width="100px">
        <el-form-item label="分区名称" prop="areaName">
          <el-input v-model="subAreaForm.areaName" placeholder="请输入" size="small" style="width: 300px" />
        </el-form-item>
        <el-form-item label="分区跟进人" prop="followUserId">
          <SelectAdminUser
            v-model="subAreaForm.followUserId"
            :job-code="AdminJobCode.A001"
            width="300px"
            @input="onSubAreaFollowUserSelect"
          />
        </el-form-item>
        <el-form-item label="下辖管理">
          <area-selector
            v-model="selectedAreaIds"
            :level="2"
            :props="areaSelectorProps"
            :disabled-city-ids="occupiedCityIds"
            collapse-tags
            size="small"
            style="width: 400px"
            clearable
          />
          <!-- 已选城市汇总表格 -->
          <el-table
            v-if="selectedProvinceStatList.length"
            :data="selectedProvinceStatList"
            border
            size="small"
            style="margin-top: 10px"
          >
            <el-table-column label="省份" prop="provinceName" width="120" />
            <el-table-column label="城市数" prop="cityCount" width="80" />
            <el-table-column label="城市明细" prop="cityNames" />
          </el-table>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="subAreaDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="subAreaSaving" @click="saveSubArea">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getTopAreaPageAPI,
  getTopAreaDetailAPI,
  addTopAreaAPI,
  editTopAreaAPI,
  deleteTopAreaAPI,
  getSubAreaDetailAPI,
  addSubAreaAPI,
  editSubAreaAPI,
  deleteSubAreaAPI,
  getOccupiedCityListAPI,
} from './api';
import AreaSelector from '@/components/area-selector/index.vue';
import regions from '@/components/area-selector/region.js';
import { getAdminList } from '@/views/components/api.js';
import SelectAdminUser from '@/views/components/select-admin-user.vue';
import { AdminJobCode } from "@/enum/dict/index.js";

// 省→市 lookup map，用于 id→name 转换
const regionMap = {}
regions.forEach((prov) => {
  regionMap[prov.regionId] = { name: prov.regionName, type: 'province' }
  ;(prov.children || []).forEach((city) => {
    regionMap[city.regionId] = { name: city.regionName, type: 'city', provinceId: prov.regionId, provinceName: prov.regionName }
  })
})

export default {
  name: 'StoreArea',
  components: { SelectAdminUser, AreaSelector },
  data() {
    return {
      AdminJobCode,
      topAreaList: [],
      // 当前激活
      activeTopAreaId: null,
      activeSubAreaId: null,
      activeTopArea: null, // 列表中当前大区对象（含subAreaList）
      topAreaDetail: null,
      subAreaDetail: null,
      // 大区表单
      topAreaDialogVisible: false,
      topAreaSaving: false,
      topAreaForm: { id: null, areaName: '', followUserId: null, followUserName: '' },
      topAreaRules: {
        areaName: [{ required: true, message: '请输入大区名称', trigger: 'blur' }],
        followUserId: [{ required: true, message: '请选择大区跟进人', trigger: 'change' }],
      },
      // 分区表单
      subAreaDialogVisible: false,
      subAreaSaving: false,
      subAreaForm: {
        id: null,
        topAreaId: null,
        topAreaName: '',
        areaName: '',
        followUserId: null,
        followUserName: '',
      },
      subAreaRules: {
        areaName: [{ required: true, message: '请输入分区名称', trigger: 'blur' }],
        followUserId: [{ required: true, message: '请选择分区跟进人', trigger: 'change' }],
      },
      // 城市选择器 - 存储 AreaSelector 的值（路径数组的数组）
      selectedAreaIds: [],
      areaSelectorProps: { multiple: true },
      // 已被其他分区占用的城市 regionId 列表（用于置灰）
      occupiedCityIds: [],
      // 管理员列表（用于查找 userName）
      adminUserMap: {},
    }
  },
  computed: {
    // 将 AreaSelector 的 value 转换为 API 需要的 cityList（只取城市级路径，即长度 >= 2）
    cityListFromSelection() {
      return this.selectedAreaIds
        .filter((path) => path.length === 2)
        .map((path) => {
          const cityInfo = regionMap[path[1]]
          return {
            provinceRegionId: path[0],
            provinceName: cityInfo?.provinceName || '',
            cityRegionId: path[1],
            cityName: cityInfo?.name || '',
          }
        })
    },
    selectedProvinceStatList() {
      const map = {}
      this.cityListFromSelection.forEach((c) => {
        if (!map[c.provinceRegionId]) {
          map[c.provinceRegionId] = { provinceName: c.provinceName, cities: [] }
        }
        map[c.provinceRegionId].cities.push(c.cityName)
      })
      return Object.values(map).map((item) => ({
        provinceName: item.provinceName,
        cityCount: item.cities.length,
        cityNames: item.cities.join('、'),
      }))
    },
  },
  mounted() {
    this.loadTopAreaList()
    this.loadAdminUserMap()
  },
  methods: {
    // ========== 管理员 ==========
    async loadAdminUserMap() {
      const res = await getAdminList({ page: 1, pageSize: 100 })
      const map = {}
      ;(res.list || []).forEach((u) => { map[u.id] = u.userName })
      this.adminUserMap = map
    },
    getUserName(id) {
      return this.adminUserMap[id] || ''
    },

    // ========== 列表 ==========
    async loadTopAreaList() {
      const res = await getTopAreaPageAPI({ page: 1, pageSize: 100 })
      this.topAreaList = res.list || []
    },

    // ========== 大区选中 ==========
    async selectTopArea(topArea) {
      this.activeTopAreaId = topArea.id
      this.activeTopArea = topArea
      this.activeSubAreaId = null
      this.subAreaDetail = null
      this.topAreaDetail = await getTopAreaDetailAPI(topArea.id)
    },

    // ========== 分区选中 ==========
    async selectSubArea(sub, topArea) {
      this.activeTopAreaId = topArea.id
      this.activeTopArea = topArea
      this.activeSubAreaId = sub.id
      this.topAreaDetail = null
      this.subAreaDetail = await getSubAreaDetailAPI(sub.id)
    },

    closeDetail() {
      this.activeTopAreaId = null
      this.activeSubAreaId = null
      this.topAreaDetail = null
      this.subAreaDetail = null
    },

    // ========== 新增大区 ==========
    openAddTopArea() {
      this.topAreaForm = { id: null, areaName: '', followUserId: null, followUserName: '' }
      this.topAreaDialogVisible = true
    },
    openEditTopArea() {
      this.topAreaForm = {
        id: this.topAreaDetail.id,
        areaName: this.topAreaDetail.areaName,
        followUserId: this.topAreaDetail.followUserId,
        followUserName: this.topAreaDetail.followUserName,
      }
      this.topAreaDialogVisible = true
    },
    onTopAreaFollowUserSelect() {
      this.topAreaForm.followUserName = this.getUserName(this.topAreaForm.followUserId)
    },
    resetTopAreaForm() {
      this.$refs.topAreaForm && this.$refs.topAreaForm.resetFields()
    },
    async saveTopArea() {
      this.$refs.topAreaForm.validate(async (valid) => {
        if (!valid) return
        this.topAreaSaving = true
        try {
          if (this.topAreaForm.id) {
            await editTopAreaAPI(this.topAreaForm)
            this.$message.success('编辑成功')
            // 刷新右侧详情
            this.topAreaDetail = await getTopAreaDetailAPI(this.topAreaForm.id)
          } else {
            await addTopAreaAPI({
              areaName: this.topAreaForm.areaName,
              followUserId: this.topAreaForm.followUserId,
            })
            this.$message.success('新增成功')
          }
          this.topAreaDialogVisible = false
          await this.loadTopAreaList()
        } finally {
          this.topAreaSaving = false
        }
      })
    },
    async handleDeleteTopArea() {
      try {
        await this.$confirm('确定删除当前大区？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        })
        await deleteTopAreaAPI(this.topAreaDetail.id)
        this.$message.success('删除成功')
        this.closeDetail()
        await this.loadTopAreaList()
      } catch (e) {
        // 取消或后端报错
      }
    },

    // ========== 分区操作 ==========
    async openAddSubArea(topArea) {
      this.subAreaForm = {
        id: null,
        topAreaId: topArea.id,
        topAreaName: topArea.areaName,
        areaName: '',
        followUserId: null,
        followUserName: '',
      }
      this.selectedAreaIds = []
      const list = await getOccupiedCityListAPI()
      this.occupiedCityIds = list.map((item) => item.cityRegionId)
      this.subAreaDialogVisible = true
    },
    async openEditSubArea() {
      const d = this.subAreaDetail
      this.subAreaForm = {
        id: d.id,
        topAreaId: d.topAreaId,
        topAreaName: d.topAreaName,
        areaName: d.areaName,
        followUserId: d.followUserId,
        followUserName: d.followUserName,
      }
      // 从详情的 provinceCityStatList 反推 selectedAreaIds（路径数组）
      const paths = []
      ;(d.provinceCityStatList || []).forEach((stat) => {
        const provRegion = regions.find((p) => p.regionId === stat.provinceRegionId)
        ;(stat.cityNameList || []).forEach((cityName) => {
          const city = provRegion?.children.find((c) => c.regionName === cityName)
          if (city) paths.push([stat.provinceRegionId, city.regionId])
        })
      })
      this.selectedAreaIds = paths
      const list = await getOccupiedCityListAPI(d.id)
      this.occupiedCityIds = list.map((item) => item.cityRegionId)
      this.subAreaDialogVisible = true
    },
    onSubAreaFollowUserSelect() {
      this.subAreaForm.followUserName = this.getUserName(this.subAreaForm.followUserId)
    },
    resetSubAreaForm() {
      this.$refs.subAreaForm && this.$refs.subAreaForm.resetFields()
      this.selectedAreaIds = []
    },
    async saveSubArea() {
      this.$refs.subAreaForm.validate(async (valid) => {
        if (!valid) return
        this.subAreaSaving = true
        try {
          const payload = {
            ...this.subAreaForm,
            cityList: this.cityListFromSelection,
          }
          if (this.subAreaForm.id) {
            await editSubAreaAPI(payload)
            this.$message.success('编辑成功')
            this.subAreaDetail = await getSubAreaDetailAPI(this.subAreaForm.id)
          } else {
            await addSubAreaAPI(payload)
            this.$message.success('新增成功')
          }
          this.subAreaDialogVisible = false
          await this.loadTopAreaList()
        } finally {
          this.subAreaSaving = false
        }
      })
    },
    async handleDeleteSubArea() {
      try {
        await this.$confirm('确定删除当前二级分区？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        })
        await deleteSubAreaAPI(this.subAreaDetail.id)
        this.$message.success('删除成功')
        this.closeDetail()
        await this.loadTopAreaList()
      } catch (e) {
        // 取消
      }
    },

  },
}
</script>

<style lang="scss" scoped>
.wrapper {
  width: 100%;
}

.area-layout {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  min-height: 600px;
}

// ===== 左侧列表 =====
.area-list-panel {
  width: 240px;
  flex-shrink: 0;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  overflow: hidden;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 2px solid #EBEEF5;
}

.panel-title {
  font-weight: bold;
  font-size: 14px;
}

.area-scroll {
  max-height: calc(100vh - 220px);
  overflow-y: auto;
  padding: 8px;
}

.top-area-item {
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: border-color 0.2s;

  &:hover {
    border-color: #409eff;
  }

  &.active {
    border-color: #409eff;
    background: #ecf5ff;
  }
}

.top-area-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
}

.top-area-name {
  font-weight: 600;
  font-size: 14px;
}

.sub-area-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px 6px 20px;
  font-size: 13px;
  color: #606266;
  cursor: pointer;
  border-top: 1px solid #f0f0f0;

  &:hover {
    background: #f5f7fa;
  }

  &.active {
    background: #ecf5ff;
    color: #409eff;
  }
}

// ===== 右侧详情 =====
.detail-panel {
  flex: 1;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  min-height: 500px;
  padding: 20px;

  &.empty {
    background: transparent;
    border-color: transparent;
  }
}

.detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.detail-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.detail-actions {
  display: flex;
  gap: 8px;
}

.info-row {
  margin-bottom: 25px;
  font-size: 14px;

  .label {
    color: #909399;
    margin-right: 12px;
  }
}

.section-title {
  font-weight: 600;
  margin-bottom: 10px;
  font-size: 14px;
}

.province-table-wrap {
  margin-top: 8px;
}

</style>
